import { inject } from '@angular/core'
import { CanActivateFn, Router, UrlTree } from '@angular/router'

import { Observable, map, of } from 'rxjs'

import { whenLoaded } from '@flowx/designer/shared/guards/when-loaded'

import { LastUsedWorkspaceService } from '../services/last-used-workspace.service'
import { landingWorkspaceId } from '../shared/workspace.utils'
import { WorkspacesAndPermissionsStore } from '../workspaces-and-permissions.store'

const NOT_ASSIGNED_URL = '/guard/not-assigned'

/**
 * Where a user goes when the URL names nowhere of their own: Solutions for an Org Owner
 * (`organization_saas_edit`, as before), the landing workspace's Projects for everyone else, and
 * "not assigned" for a user without workspaces. A link the user can open never comes here, so it
 * wins over every rule.
 *
 * Call it in an injection context.
 */
export function resolveLanding(): Observable<UrlTree> {
  const router = inject(Router)
  const workspacesAndPermissionsStore = inject(WorkspacesAndPermissionsStore)
  const lastUsedWorkspaceService = inject(LastUsedWorkspaceService)

  if (workspacesAndPermissionsStore.userIsOrganizationSaasAdmin()) {
    return of(router.createUrlTree(['/guard', 'organisation-admin', 'projects']))
  }

  return whenLoaded(lastUsedWorkspaceService.loading).pipe(
    map(() => {
      const workspaceId = landingWorkspaceId(
        workspacesAndPermissionsStore.availableWorkspacesForUser(),
        lastUsedWorkspaceService.workspaceId()
      )

      if (workspaceId) {
        lastUsedWorkspaceService.saveLandingPick(workspaceId)
      }

      return workspaceId
        ? router.createUrlTree(['/guard', 'workspace', workspaceId, 'projects'])
        : router.parseUrl(NOT_ASSIGNED_URL)
    })
  )
}

/**
 * A guard rather than a redirect: redirects run while routes are matched, before the auth guard,
 * when nothing about the user is loaded yet.
 */
export const landingGuard: CanActivateFn = (): Observable<UrlTree> => resolveLanding()

/**
 * "Not assigned" is the landing's last fallback, for a user with no workspace who isn't an Org
 * Owner, so it only shows to a user the landing would send there. Anyone else who reaches it, by a
 * bookmark, Back, or a reload after being given access, is redirected to where they land instead:
 * the landing's `UrlTree` is returned rather than `true`.
 */
export const notAssignedGuard: CanActivateFn = (): Observable<true | UrlTree> =>
  resolveLanding().pipe(
    map((landing) => (landing.toString() === NOT_ASSIGNED_URL ? true : landing))
  )
